/**
 * Which track a version belongs on once a project is split into structures.
 * Pure helpers shared by the Files rail, the upload modal, and version assignment.
 */

import type { ProjectFileStream, ProjectFileStreamKind } from '../types';
import { structureSetKey } from './structureSelectionLabel';

/** Left-rail order inside every structure group. */
export const STREAM_KIND_RAIL_ORDER: ProjectFileStreamKind[] = [
  'three_d_html',
  'two_d_pdf',
  'timber_list_csv',
  'three_d_raw',
  'hardware_list_csv',
  'general',
];

export function structureTrackKindLabel(kind: ProjectFileStreamKind): string {
  switch (kind) {
    case 'three_d_html':
      return 'HTML';
    case 'two_d_pdf':
      return '2Ds';
    case 'timber_list_csv':
      return 'Timber List';
    case 'three_d_raw':
      return '3D';
    case 'hardware_list_csv':
      return 'Hardware';
    default:
      return 'Files';
  }
}

export function compareStreamsByRailOrder(a: ProjectFileStream, b: ProjectFileStream): number {
  const rank = (kind: ProjectFileStreamKind) => {
    const index = STREAM_KIND_RAIL_ORDER.indexOf(kind);
    return index === -1 ? STREAM_KIND_RAIL_ORDER.length : index;
  };
  const byKind = rank(a.streamKind) - rank(b.streamKind);
  if (byKind !== 0) return byKind;
  return (a.title || '').localeCompare(b.title || '');
}

/** True once any track is tagged to structures or the pre-split root has been locked. */
export function projectIsSplitIntoStructures(
  streams: Pick<ProjectFileStream, 'wholeProjectRoot' | 'structureIds'>[],
): boolean {
  return streams.some(s => s.wholeProjectRoot || (s.structureIds ?? []).length > 0);
}

export interface StructureTrackPlacement {
  /** Existing track of this file type, or null when these structures still need one. */
  streamId: string | null;
  structureIds: string[];
}

/**
 * Cover each requested structure with one track of `kind`.
 * A track tagged to a single structure wins over a wider track. A track that
 * already covers several of the requested structures is used once. The shared
 * pre-split root is never a destination.
 */
export function matchStructureTracks(
  streams: ProjectFileStream[],
  kind: ProjectFileStreamKind,
  structureIds: string[],
): StructureTrackPlacement[] {
  const remaining = new Set(structureIds.filter(Boolean));
  const candidates = streams.filter(
    s => !!s.id && s.streamKind === kind && !s.wholeProjectRoot && (s.structureIds ?? []).length > 0,
  );
  const used = new Set<string>();
  const out: StructureTrackPlacement[] = [];

  for (const id of structureIds) {
    if (!remaining.has(id)) continue;
    const exact = candidates.find(
      s => !used.has(s.id!) && (s.structureIds ?? []).length === 1 && s.structureIds![0] === id,
    );
    const cover =
      exact
      ?? candidates
        .filter(s => !used.has(s.id!) && (s.structureIds ?? []).includes(id))
        .sort((a, b) => (a.structureIds?.length ?? 0) - (b.structureIds?.length ?? 0))[0];
    if (!cover?.id) {
      remaining.delete(id);
      out.push({ streamId: null, structureIds: [id] });
      continue;
    }
    const covered = (cover.structureIds ?? []).filter(sid => remaining.has(sid));
    covered.forEach(sid => remaining.delete(sid));
    used.add(cover.id);
    out.push({ streamId: cover.id, structureIds: covered.length > 0 ? covered : [id] });
  }

  return out;
}

/**
 * One destination per selected structure group. A group that already has a
 * track of this file type reuses it; otherwise the upload creates one track
 * for the whole group, not one track per structure inside it.
 * A selection that is not a set of those groups falls back to per-structure matching.
 */
export function placementsForUploadGroups(
  streams: ProjectFileStream[],
  kind: ProjectFileStreamKind,
  selectedStructureIds: string[],
  groups: ReadonlyArray<{ id: string; structureIds: string[]; customLabel?: string }>,
): Array<StructureTrackPlacement & { groupLabel?: string }> {
  const selectedIds = selectedStructureIds.filter(Boolean);
  const selected = new Set(selectedIds);
  const chosen = groups.filter(
    group => group.structureIds.length > 0 && group.structureIds.every(id => selected.has(id)),
  );
  const covered = new Set(chosen.flatMap(group => group.structureIds));
  const exact = selected.size > 0
    && selectedIds.every(id => covered.has(id))
    && covered.size === selected.size;
  if (!exact) return matchStructureTracks(streams, kind, selectedStructureIds);

  return chosen.map(group => {
    const key = structureSetKey(group.structureIds);
    const track = streams.find(stream => {
      const ids = (stream.structureIds ?? []).filter(Boolean);
      return !!stream.id
        && stream.streamKind === kind
        && !stream.wholeProjectRoot
        && ids.length > 0
        && structureSetKey(ids) === key;
    });
    return {
      streamId: track?.id ?? null,
      structureIds: [...group.structureIds],
      ...(group.customLabel ? { groupLabel: group.customLabel } : {}),
    };
  });
}
